import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, GraduationCap, Store, ShoppingBag, Briefcase, UserCog, Users, Globe, ShieldCheck } from 'lucide-react';
import WasslaLogo from './WasslaLogo';

/**
 * Profile Type Picker - App Onboarding Modal
 * Fully bilingual (AR/EN) with proper RTL support
 * Shows once per user to personalize content
 * 
 * Profile Type Routing Logic:
 * - student: /student-hub (books, CV, opportunities)
 * - seller: /shop (my ads dashboard, post ad)
 * - service: /shop?category=services (service providers)
 * - professional: /student-hub?tab=opportunities (CV builder + jobs)
 * - general: / (normal home, no prioritization)
 */

// Storage keys
const STORAGE_KEY = 'wassla_profile_type';
const STORAGE_TIMESTAMP_KEY = 'wassla_profile_type_set_at';
const SESSION_DISMISSED_KEY = 'wassla_profile_picker_dismissed_session';
const LANG_STORAGE_KEY = 'wassla_lang';

// Profile type configuration
export const PROFILE_TYPES = [
  {
    key: 'seller',
    icon: Store,
    route: '/shop',
    color: '#f59e0b',
    defaultLanding: '/shop',
    authLanding: '/my-ads',
    priorities: ['post_ad', 'seller_tools', 'inventory', 'marketplace'],
    showPostAdButton: true,
  },
  {
    key: 'buyer',
    icon: ShoppingBag,
    route: '/',
    color: '#3b82f6',
    defaultLanding: '/',
    priorities: ['browse', 'deals', 'shop'],
    noPrioritization: false,
  },
  {
    key: 'student',
    icon: GraduationCap,
    route: '/student-hub',
    color: '#8b5cf6',
    defaultLanding: '/student-hub',
    priorities: ['books', 'notes', 'cv'],
    enableAcademicFilters: true,
  },
  {
    key: 'service',
    icon: UserCog,
    route: '/shop?category=services',
    color: '#10b981',
    defaultLanding: '/shop?category=services',
    priorities: ['booking', 'services', 'requests'],
    showServiceCategories: true,
  },
  {
    key: 'professional',
    icon: Briefcase,
    route: '/student-hub/cv',
    color: '#6366f1',
    defaultLanding: '/student-hub/cv',
    priorities: ['cv_builder', 'career'],
    prioritizeCVBuilder: true,
  },
  {
    key: 'general',
    icon: Users,
    route: '/',
    color: '#6b7280',
    defaultLanding: '/',
    priorities: [],
    noPrioritization: true,
  },
];

/**
 * Detect current language with fallback chain:
 * 1. i18n state (if available)
 * 2. localStorage "wassla_lang"
 * 3. localStorage "lang" (legacy)
 * 4. navigator.language
 * 5. default = "ar" (Sudan default)
 */
export function detectLanguage() {
  try {
    // Check wassla_lang first
    const wasslaLang = localStorage.getItem(LANG_STORAGE_KEY);
    if (wasslaLang === 'ar' || wasslaLang === 'en') return wasslaLang;
    
    // Check legacy lang key
    const legacyLang = localStorage.getItem('lang');
    if (legacyLang === 'ar' || legacyLang === 'en') return legacyLang;
    
    // Check navigator
    const navLang = navigator.language?.toLowerCase();
    if (navLang?.startsWith('ar')) return 'ar';
    if (navLang?.startsWith('en')) return 'en';
    
    // Default for Sudan
    return 'ar';
  } catch {
    return 'ar';
  }
}

/**
 * Save language preference
 */
export function saveLanguagePreference(lang) {
  try {
    if (lang === 'ar' || lang === 'en') {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
      localStorage.setItem('lang', lang); // Also save to legacy key for compatibility
      return true;
    }
  } catch {}
  return false;
}

/**
 * Get stored profile type
 */
export function getStoredProfileType() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Set profile type with timestamp
 */
export function setStoredProfileType(type) {
  try {
    localStorage.setItem(STORAGE_KEY, type);
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, new Date().toISOString());
    return true;
  } catch {
    return false;
  }
}

/**
 * Clear profile type (for reset)
 */
export function clearStoredProfileType() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
    sessionStorage.removeItem(SESSION_DISMISSED_KEY);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if picker should be shown
 * Returns true only if: no stored type AND no completed selection AND not dismissed this session
 */
export function shouldShowProfilePicker() {
  try {
    const storedType = localStorage.getItem(STORAGE_KEY);
    const completedSelection = localStorage.getItem('hasCompletedProfileSelection');
    const dismissedSession = sessionStorage.getItem(SESSION_DISMISSED_KEY);
    return !storedType && !completedSelection && !dismissedSession;
  } catch {
    return false;
  }
}

/**
 * Mark picker as dismissed for this session
 */
export function dismissPickerSession() {
  try {
    sessionStorage.setItem(SESSION_DISMISSED_KEY, 'true');
  } catch {}
}

/**
 * Best-effort authenticated POST to a /api/me/* flag endpoint. Same-origin
 * aware (see iter263): on production deploys where the baked-in
 * REACT_APP_BACKEND_URL is cross-origin, fall back to the current origin.
 */
async function postAuthedMeFlag(path) {
  try {
    let token = null;
    try { token = localStorage.getItem('token'); } catch (_) { /* no storage */ }
    if (!token || token === 'null' || token === 'undefined') return;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
    const winHost = (typeof window !== 'undefined' && window.location)
      ? (window.location.hostname || '').toLowerCase()
      : '';
    const isPreviewHost =
      winHost === 'localhost' || winHost === '127.0.0.1' ||
      winHost.endsWith('.preview.emergentagent.com') ||
      winHost.endsWith('.emergentagent.com');
    let apiBase = base;
    try {
      if (base && new URL(base).origin !== origin && !isPreviewHost) {
        apiBase = '';
      }
    } catch (_) { /* keep base */ }
    await fetch(`${apiBase}${path}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${String(token).trim()}` },
      credentials: 'include',
    });
  } catch (_e) {
    // Best-effort — never block UX on a network blip.
  }
}

/**
 * Get profile config by key
 */
export function getProfileConfig(profileKey) {
  return PROFILE_TYPES.find(p => p.key === profileKey) || PROFILE_TYPES[5];
}

/**
 * Get the default landing route for a profile type
 */
export function getProfileRoute(profileKey, isAuthenticated = false) {
  const config = getProfileConfig(profileKey);
  if (isAuthenticated && config?.authLanding) {
    return config.authLanding;
  }
  return config?.defaultLanding || config?.route || '/';
}

/**
 * Check if a profile type should show academic level filtering
 */
export function shouldShowAcademicFilters(profileKey) {
  const config = getProfileConfig(profileKey);
  return config?.enableAcademicFilters || false;
}

/**
 * Check if a profile type should prioritize CV Builder
 */
export function shouldPrioritizeCVBuilder(profileKey) {
  const config = getProfileConfig(profileKey);
  return config?.prioritizeCVBuilder || profileKey === 'student';
}

/**
 * Check if a profile type should show prominent "Post Ad" button
 */
export function shouldShowPostAdButton(profileKey) {
  const config = getProfileConfig(profileKey);
  return config?.showPostAdButton || profileKey === 'seller';
}

/**
 * Get priority content areas for a profile type
 */
export function getProfilePriorities(profileKey) {
  const config = getProfileConfig(profileKey);
  return config?.priorities || [];
}

/**
 * Profile Type Picker Modal Component
 * Fully bilingual with in-modal language switching
 */
export default function ProfileTypePicker({ 
  isOpen, 
  onClose, 
  onSelect, 
  showCloseButton = true,
}) {
  const { t, i18n } = useTranslation();
  
  // Initialize language from detection
  const [currentLang, setCurrentLang] = useState(() => {
    // Prefer i18n language, fall back to detection
    const i18nLang = i18n.language?.split('-')[0];
    if (i18nLang === 'ar' || i18nLang === 'en') return i18nLang;
    return detectLanguage();
  });
  
  const isRTL = currentLang === 'ar';
  const [selectedType, setSelectedType] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const modalRef = useRef(null);

  // Sync with i18n on mount
  useEffect(() => {
    const i18nLang = i18n.language?.split('-')[0];
    if (i18nLang === 'ar' || i18nLang === 'en') {
      setCurrentLang(i18nLang);
    }
  }, [i18n.language]);

  // Handle language switch
  const handleLanguageSwitch = useCallback(() => {
    const newLang = currentLang === 'ar' ? 'en' : 'ar';
    setCurrentLang(newLang);
    
    // Update i18n
    i18n.changeLanguage(newLang);
    
    // Save preference
    saveLanguagePreference(newLang);
    
    // Update document direction
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  }, [currentLang, i18n]);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen && !isClosing) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isClosing]);

  // iter270 — defensive backend-persistence of the dismissal.
  //
  // Why this lives INSIDE the picker (not just in the parent's onClose):
  // some mount sites (older entry pages, deep links) wired `onClose` to
  // a noop or to a function that didn't reach `/api/me/profile-type-dismiss`,
  // so closing the modal only updated `sessionStorage` and the modal
  // popped back up on the next login. Now the picker itself always
  // calls the backend, idempotently, no matter how it was closed.
  //
  // Effect: setting `onboarding_completed=true` on the user record.
  // /api/auth/me then returns `needs_onboarding=false` and the gate
  // in AuthContext / Home stays shut.
  //
  // Ref guard prevents duplicate POSTs from StrictMode double-effects
  // or from the close → confirm callback chain firing both paths.
  const dismissSentRef = useRef(false);
  const persistDismissToBackend = useCallback(async () => {
    if (dismissSentRef.current) return;
    dismissSentRef.current = true;
    await postAuthedMeFlag('/api/me/profile-type-dismiss');
  }, []);

  // Once-only "seen" marker — fired the moment the picker is first displayed
  // so the intro onboarding can never re-appear on a later sign-in, even if
  // the user force-closes the app without tapping Confirm / Later / X.
  // Idempotent server-side; ref guard stops StrictMode double-fire.
  const seenSentRef = useRef(false);
  useEffect(() => {
    if (!isOpen || seenSentRef.current) return;
    seenSentRef.current = true;
    postAuthedMeFlag('/api/me/onboarding-seen');
  }, [isOpen]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setIsAnimating(false);
    dismissPickerSession();
    // Persist dismissal to backend so the picker doesn't pop back up next login.
    persistDismissToBackend();

    setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 280);
  }, [onClose, persistDismissToBackend]);

  // Handle backdrop click
  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  }, [handleClose]);

  // iter270 — Escape key also dismisses (was previously ignored,
  // letting users press ESC to close the modal without persisting).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (ev) => {
      if (ev.key === 'Escape' || ev.key === 'Esc') {
        handleClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, handleClose]);

  // Handle card selection
  const handleSelect = useCallback((type) => {
    setSelectedType(type.key);
  }, []);

  // Handle confirm
  const handleConfirm = useCallback(() => {
    if (selectedType) {
      setStoredProfileType(selectedType);
      // Also save language preference on confirm
      saveLanguagePreference(currentLang);
      onSelect?.(selectedType);
      // iter270 — also flip `onboarding_completed=true` server-side
      // so the picker can never re-appear (the parent's onSelect
      // handler does call setAccountType, but the dedicated dismiss
      // route is idempotent and an explicit safety net).
      persistDismissToBackend();

      setIsClosing(true);
      setIsAnimating(false);
      setTimeout(() => {
        setIsClosing(false);
        onClose?.();
      }, 200);
    }
  }, [selectedType, currentLang, onSelect, onClose, persistDismissToBackend]);

  // Handle skip/later
  const handleSkip = useCallback(() => {
    handleClose();
  }, [handleClose]);

  if (!isOpen && !isClosing) return null;

  // Get localized text - using i18n keys
  const getText = (key) => {
    return t(`profilePicker.${key}`, { defaultValue: '' }) || t(key, { defaultValue: key });
  };

  // Profile type translations
  const getProfileText = (profileKey, field) => {
    const key = `profilePicker.types.${profileKey}.${field}`;
    return t(key, { defaultValue: '' });
  };

  return (
    <div 
      className={`profile-picker-overlay ${isAnimating ? 'animate' : ''}`}
      onClick={handleBackdropClick}
      dir={isRTL ? 'rtl' : 'ltr'}
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-picker-title"
      data-testid="profile-picker-modal"
    >
      <div 
        ref={modalRef}
        className="profile-picker-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Language Switch Button */}
        <button
          type="button"
          className="picker-lang-btn"
          onClick={handleLanguageSwitch}
          aria-label={t('ptp.text')}
          data-testid="profile-picker-lang-switch"
        >
          <Globe size={14} strokeWidth={2} />
          <span>{t('ptp.text2')}</span>
        </button>

        {/* Close button */}
        {showCloseButton && (
          <button
            type="button"
            className="picker-close-btn"
            onClick={handleClose}
            aria-label={t('rolesele.close')}
            data-testid="profile-picker-close"
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        )}

        {/* Handle bar for bottom sheet feel */}
        <div className="picker-handle" aria-hidden="true" />

        {/* Hero — premium welcome area. Builds trust before presenting options.
            Copy explicitly reflects real product scope (buy/sell/services);
            no jobs / scholarships / opportunities promised. */}
        <div className="picker-hero" data-testid="picker-hero">
          <div className="picker-hero-brand" aria-hidden="true">
            <WasslaLogo size={44} />
          </div>
          <h2 id="profile-picker-title" className="picker-hero-title" data-testid="picker-hero-title">
            {getText('heroTitle')}
          </h2>
          <p className="picker-hero-subtitle" data-testid="picker-hero-subtitle">
            {getText('heroSubtitle')}
          </p>
          <p className="picker-hero-helper" data-testid="picker-hero-helper">
            {getText('supportingLine')}
          </p>
        </div>

        {/* Options */}
        <div className="picker-options" role="radiogroup" aria-label={getText('selectLabel')}>
          {PROFILE_TYPES.map((type) => {
            const IconComponent = type.icon;
            const isSelected = selectedType === type.key;
            const label = getProfileText(type.key, 'label');
            const desc = getProfileText(type.key, 'desc');
            
            return (
              <button
                key={type.key}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`picker-option ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelect(type)}
                data-testid={`profile-option-${type.key}`}
                style={{ '--option-color': type.color }}
              >
                <span className="option-icon-wrapper" style={{ backgroundColor: `${type.color}15` }}>
                  <IconComponent 
                    size={26} 
                    strokeWidth={2} 
                    color={type.color}
                    aria-hidden="true"
                  />
                </span>
                <div className="option-content">
                  <span className="option-label">{label}</span>
                  <span className="option-desc">{desc}</span>
                </div>
                {isSelected && (
                  <span className="option-check" aria-hidden="true">
                    <Check size={18} strokeWidth={3} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Trust strip — subtle emotional reassurance above CTA */}
        <div className="picker-trust" data-testid="picker-trust">
          <span className="picker-trust-icon" aria-hidden="true">
            <ShieldCheck size={16} strokeWidth={2.2} />
          </span>
          <span className="picker-trust-text">
            <strong>{getText('trustTitle')}</strong>
            <span className="picker-trust-subline">{getText('trustSubline')}</span>
          </span>
        </div>

        {/* Actions */}
        <div className="picker-actions">
          <button
            type="button"
            className="picker-btn picker-btn-primary"
            onClick={handleConfirm}
            disabled={!selectedType}
            data-testid="profile-confirm"
          >
            {getText('confirm')}
          </button>
          <button
            type="button"
            className="picker-btn picker-btn-secondary"
            onClick={handleSkip}
            data-testid="profile-skip"
          >
            {getText('later')}
          </button>
        </div>

        {/* Bottom brand signature — feels like a premium finish */}
        <div className="picker-brand-signature" data-testid="picker-brand-signature" aria-hidden="true">
          <WasslaLogo size={26} />
          <span className="picker-brand-slogan">{getText('slogan')}</span>
        </div>
      </div>

      <style>{`
        /* ========================================
           Profile Type Picker - Bilingual RTL/LTR
           ======================================== */
        
        .profile-picker-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          z-index: 9999;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          opacity: 0;
          transition: opacity 250ms ease-out;
          padding: 0 0.5rem;
        }

        .profile-picker-overlay.animate {
          opacity: 1;
        }

        .profile-picker-modal {
          position: relative;
          width: 100%;
          max-width: 440px;
          max-height: 90vh;
          background: #FFFFFF;
          border-radius: 1.5rem 1.5rem 0 0;
          padding: 0.875rem 1.25rem 2rem;
          overflow-y: auto;
          transform: translateY(100%);
          transition: transform 300ms cubic-bezier(0.32, 0.72, 0, 1);
          box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.25);
          border: 1px solid rgba(0, 0, 0, 0.08);
        }

        .profile-picker-overlay.animate .profile-picker-modal {
          transform: translateY(0);
        }

        /* Dark mode */
        html.dark .profile-picker-modal {
          background: #1e293b;
          border-color: rgba(255, 255, 255, 0.1);
        }

        /* Language switch button - SMALL and unobtrusive */
        .picker-lang-btn {
          position: absolute;
          top: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          height: 28px;
          padding: 0 0.625rem;
          background: rgba(255, 255, 255, 0.85);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 14px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #475569;
          cursor: pointer;
          transition: all 150ms ease;
          z-index: 10;
        }

        /* LTR: lang button on left */
        [dir="ltr"] .picker-lang-btn {
          left: 1rem;
          right: auto;
        }

        /* RTL: lang button on right */
        [dir="rtl"] .picker-lang-btn {
          right: 1rem;
          left: auto;
        }

        .picker-lang-btn:hover {
          background: #e2e8f0;
          border-color: #cbd5e1;
          color: #334155;
        }

        html.dark .picker-lang-btn {
          background: #334155;
          border-color: #475569;
          color: #94a3b8;
        }

        html.dark .picker-lang-btn:hover {
          background: #475569;
          color: #f1f5f9;
        }

        /* Close button - opposite side of lang button */
        .picker-close-btn {
          position: absolute;
          top: 1rem;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #f1f5f9;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #64748b;
          transition: all 150ms ease;
          z-index: 10;
        }

        /* LTR: close button on right */
        [dir="ltr"] .picker-close-btn {
          right: 1rem;
          left: auto;
        }

        /* RTL: close button on left */
        [dir="rtl"] .picker-close-btn {
          left: 1rem;
          right: auto;
        }

        .picker-close-btn:hover {
          background: #e2e8f0;
          color: #334155;
        }

        html.dark .picker-close-btn {
          background: #334155;
          color: #94a3b8;
        }

        html.dark .picker-close-btn:hover {
          background: #475569;
          color: #f1f5f9;
        }

        /* Handle bar */
        .picker-handle {
          width: 40px;
          height: 4px;
          background: #cbd5e1;
          border-radius: 2px;
          margin: 0 auto 1.25rem;
        }

        html.dark .picker-handle {
          background: #475569;
        }

        /* ── Hero header — premium welcome area ───────────────────────
           Replaces the older .picker-header. Uses soft elevation and a
           subtle brand halo to feel like a receptionist's first greeting. */
        .picker-hero {
          position: relative;
          text-align: center;
          margin: 0.25rem 0 1.5rem;
          padding: 0.25rem 1rem 0;
        }
        .picker-hero-brand {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 64px;
          height: 64px;
          margin: 0 auto 0.875rem;
          background: #ffffff;
          border-radius: 18px;
          box-shadow:
            0 1px 2px rgba(15, 23, 42, 0.06),
            0 8px 24px rgba(31, 111, 58, 0.12);
          border: 1px solid rgba(31, 111, 58, 0.12);
        }
        html.dark .picker-hero-brand {
          background: #0f172a;
          box-shadow:
            0 1px 2px rgba(0, 0, 0, 0.4),
            0 8px 24px rgba(31, 111, 58, 0.22);
          border-color: rgba(52, 211, 153, 0.22);
        }
        .picker-hero-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0F172A;
          margin: 0 0 0.5rem;
          line-height: 1.25;
          letter-spacing: -0.01em;
        }
        .picker-hero-subtitle {
          font-size: 0.95rem;
          color: #334155;
          margin: 0 0 0.625rem;
          line-height: 1.55;
          max-width: 32ch;
          margin-inline: auto;
        }
        .picker-hero-helper {
          font-size: 0.82rem;
          color: #64748b;
          margin: 0;
          line-height: 1.5;
        }
        html.dark .picker-hero-title { color: #f1f5f9; }
        html.dark .picker-hero-subtitle { color: #cbd5e1; }
        html.dark .picker-hero-helper { color: #94a3b8; }

        /* Legacy header styles kept as a no-op fallback in case any external
           caller still renders .picker-header — prevents visual regressions
           elsewhere. */
        .picker-header {
          text-align: center;
          margin-bottom: 1.5rem;
          padding: 0.5rem 2.5rem 0;
        }

        .picker-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #0F172A;
          margin: 0 0 0.5rem;
          line-height: 1.3;
        }

        html.dark .picker-title {
          color: #f1f5f9;
        }

        .picker-subtitle {
          font-size: 0.875rem;
          color: #334155;
          margin: 0;
          line-height: 1.5;
          white-space: pre-line;
        }

        html.dark .picker-subtitle {
          color: #94a3b8;
        }

        /* Options container */
        .picker-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        /* Option cards with RTL support */
        .picker-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.125rem;
          background: #F8FAFC;
          border: 2px solid #e2e8f0;
          border-radius: 0.875rem;
          cursor: pointer;
          transition: all 180ms ease;
          text-align: start;
          position: relative;
        }

        /* RTL: reverse flex direction */
        [dir="rtl"] .picker-option {
          flex-direction: row;
        }

        html.dark .picker-option {
          background: #1e293b;
          border-color: #334155;
        }

        .picker-option:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        html.dark .picker-option:hover {
          background: #334155;
          border-color: #475569;
        }

        .picker-option.selected {
          background: #FFFFFF;
          border-color: var(--option-color, #3b82f6);
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);
        }

        html.dark .picker-option.selected {
          background: #1e293b;
          border-color: var(--option-color, #60a5fa);
          box-shadow: 0 4px 16px rgba(96, 165, 250, 0.2);
        }

        .picker-option:active {
          transform: scale(0.98);
        }

        /* Icon wrapper */
        .option-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        /* Option content - proper text alignment */
        .option-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          min-width: 0;
        }

        [dir="rtl"] .option-content {
          text-align: right;
        }

        [dir="ltr"] .option-content {
          text-align: left;
        }

        .option-label {
          font-size: 1rem;
          font-weight: 600;
          color: #0F172A;
        }

        html.dark .option-label {
          color: #f1f5f9;
        }

        .option-desc {
          font-size: 0.8125rem;
          color: #64748b;
          line-height: 1.4;
        }

        html.dark .option-desc {
          color: #94a3b8;
        }

        /* Selected check mark */
        .option-check {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--option-color, #3b82f6);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        /* Actions */
        .picker-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .picker-btn {
          width: 100%;
          padding: 0.9375rem 1.5rem;
          border-radius: 0.75rem;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 150ms ease;
          border: none;
        }

        .picker-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }

        .picker-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
        }

        .picker-btn-primary:disabled {
          background: #cbd5e1;
          color: #94a3b8;
          box-shadow: none;
          cursor: not-allowed;
        }

        html.dark .picker-btn-primary:disabled {
          background: #334155;
          color: #64748b;
        }

        .picker-btn-secondary {
          background: transparent;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }

        .picker-btn-secondary:hover {
          background: #f1f5f9;
          color: #334155;
          border-color: #cbd5e1;
        }

        html.dark .picker-btn-secondary {
          color: #94a3b8;
          border-color: #334155;
        }

        html.dark .picker-btn-secondary:hover {
          background: #334155;
          color: #f1f5f9;
        }

        /* Desktop adjustments */
        @media (min-width: 640px) {
          .profile-picker-overlay {
            align-items: center;
            padding: 1rem;
          }

          .profile-picker-modal {
            border-radius: 1.25rem;
            max-height: 85vh;
          }

          .picker-handle {
            display: none;
          }
        }

        /* Ensure safe area on iOS */
        @supports (padding-bottom: env(safe-area-inset-bottom)) {
          .profile-picker-modal {
            padding-bottom: calc(2rem + env(safe-area-inset-bottom));
          }
        }

        /* ── Trust strip ─────────────────────────────────────────────
           Subtle reassurance above the CTA. Uses a quiet brand tint so
           it doesn't compete with the action buttons. */
        .picker-trust {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.625rem;
          width: 100%;
          margin: 0 0 0.875rem;
          padding: 0.625rem 0.875rem;
          background: rgba(31, 111, 58, 0.06);
          border: 1px solid rgba(31, 111, 58, 0.14);
          border-radius: 12px;
          text-align: start;
        }
        .picker-trust-icon {
          display: inline-flex;
          width: 28px;
          height: 28px;
          align-items: center;
          justify-content: center;
          background: rgba(31, 111, 58, 0.12);
          color: #1F6F3A;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .picker-trust-text {
          display: inline-flex;
          flex-direction: column;
          line-height: 1.3;
          font-size: 0.82rem;
          color: #334155;
        }
        .picker-trust-text strong {
          font-size: 0.85rem;
          font-weight: 600;
          color: #0F172A;
          margin-bottom: 2px;
        }
        .picker-trust-subline {
          font-size: 0.72rem;
          color: #64748b;
          letter-spacing: 0.01em;
        }
        html.dark .picker-trust {
          background: rgba(52, 211, 153, 0.08);
          border-color: rgba(52, 211, 153, 0.22);
        }
        html.dark .picker-trust-icon {
          background: rgba(52, 211, 153, 0.18);
          color: #6EE7B7;
        }
        html.dark .picker-trust-text { color: #cbd5e1; }
        html.dark .picker-trust-text strong { color: #f1f5f9; }
        html.dark .picker-trust-subline { color: #94a3b8; }

        /* ── Brand signature — premium finish at the bottom ───────── */
        .picker-brand-signature {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.625rem;
          margin-top: 1.25rem;
          padding-top: 1rem;
          border-top: 1px dashed rgba(100, 116, 139, 0.28);
          color: #94a3b8;
        }
        .picker-brand-slogan {
          font-size: 0.78rem;
          letter-spacing: 0.02em;
          color: #64748b;
        }
        html.dark .picker-brand-signature {
          border-top-color: rgba(148, 163, 184, 0.2);
          color: #64748b;
        }
        html.dark .picker-brand-slogan { color: #94a3b8; }
      `}</style>
    </div>
  );
}

// Named export for settings integration
export { ProfileTypePicker };
